import { Box } from 'designSystem/components/Box'
import { PreviewDocumentDetailsSection } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/PreviewDocumentDetailsSection'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { StackedMetadataEditSections } from 'features/knowledgeDocumentViewer/components/metadata/StackedMetadataEditSections'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useStackedMetadataEditControllers } from 'features/knowledgeDocumentViewer/hooks/useStackedMetadataEditControllers'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type { DocumentMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { FamilyDocumentMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

const noopDocumentMetadataSave: (
  values: DocumentMetadataUserEditValues
) => Promise<void> = async () => {}

const noopDocumentMetadataFamilySave: (
  values: DocumentMetadataUserEditValues,
  members: readonly FamilyDocumentMetadataSaveMember[]
) => Promise<void> = async () => {}

const BesideSubjectShell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 10rem;
  overflow-x: hidden;
  overflow-y: auto;
`

export function DocumentMetadataSection({
  besideSubject = false,
  scrollWithParent = false,
}: {
  besideSubject?: boolean
  scrollWithParent?: boolean
}) {
  const {
    currentDocumentId,
    currentPageNumber,
    canViewUserEdits,
    canEditUserEdits,
    isGeckoUser,
    handleSelectDocument,
    addDocumentTab,
  } = useKnowledgeDocumentUXContext()
  const { document: selectedDocument } =
    useSystemDocumentByBqId(currentDocumentId)

  const bqDocumentId = selectedDocument
    ? getSystemDocumentBqId(selectedDocument)
    : undefined
  const {
    showDocumentDetails,
    showPageDetails,
    documentUserEdit,
    isDocumentUserEditLoading,
    pageEntry,
    isPageLoading,
    pageUserEdit,
    isPageUserEditLoading,
    isSavingDocumentEdit,
    saveDocumentMetadata,
    saveDocumentMetadataFamily,
    isSavingPageEdit,
    savePageMetadata,
    savePageMetadataFamily,
    handleDocumentEditingChange,
    handlePageEditingChange,
    isEditingMetadata,
  } = useStackedMetadataEditControllers({
    bqDocumentId,
    systemDocumentEntryId: selectedDocument?.entry.id,
    systemDocument: selectedDocument,
    pageNum: currentPageNumber,
    userEditsEnabled: canViewUserEdits,
    pageDetailsEnabled: canViewUserEdits,
    resetKey: currentDocumentId,
  })

  const documentTableRow = useMemo(() => {
    if (!selectedDocument) return null
    return transformSystemDocumentToTableRow(
      selectedDocument,
      canViewUserEdits ? documentUserEdit?.entry.data.values : undefined
    )
  }, [canViewUserEdits, documentUserEdit, selectedDocument])

  const handleSelectVersion = useCallback(
    (member: RevisionFamilyMemberView) => {
      handleSelectDocument(
        member.bqDocumentId,
        undefined,
        undefined,
        undefined,
        member.pageNum
      )
    },
    [handleSelectDocument]
  )

  const handleOpenVersionInNewTab = useCallback(
    (member: RevisionFamilyMemberView) => {
      addDocumentTab({
        documentId: member.bqDocumentId,
        pageNumber: member.pageNum,
      })
    },
    [addDocumentTab]
  )

  if (!currentDocumentId || !selectedDocument || !documentTableRow) {
    return null
  }

  const fillAvailable = isEditingMetadata && !scrollWithParent

  const section = (
    <Box
      display='flex'
      direction='column'
      flex={besideSubject || scrollWithParent ? 'none' : 'one'}
      shrink={false}
      minHeight='none'
      overflow={
        scrollWithParent
          ? 'visible'
          : isEditingMetadata
            ? 'hidden'
            : besideSubject
              ? 'visible'
              : 'yAuto'
      }
      border='top'
      paddingTop='lg'
      paddingBottom={isEditingMetadata ? 'xl' : 'lg'}>
      <StackedMetadataEditSections
        showDocumentDetails={showDocumentDetails}
        showPageDetails={showPageDetails}
        systemDocument={selectedDocument}
        documentUserEdit={canViewUserEdits ? documentUserEdit : undefined}
        isDocumentLoading={canViewUserEdits ? isDocumentUserEditLoading : false}
        canEditDocument={canEditUserEdits && Boolean(bqDocumentId)}
        isSavingDocumentEdit={isSavingDocumentEdit}
        pageNum={currentPageNumber}
        pageEntry={pageEntry}
        pageUserEdit={pageUserEdit}
        isPageLoading={isPageLoading || isPageUserEditLoading}
        canEditPage={canEditUserEdits && Boolean(bqDocumentId)}
        isSavingPageEdit={isSavingPageEdit}
        currentBqDocumentId={bqDocumentId}
        currentFilename={selectedDocument.entry.data.filename}
        onSaveDocument={
          canEditUserEdits ? saveDocumentMetadata : noopDocumentMetadataSave
        }
        onSaveDocumentFamily={
          canEditUserEdits
            ? saveDocumentMetadataFamily
            : noopDocumentMetadataFamilySave
        }
        onSavePage={savePageMetadata}
        onSavePageFamily={savePageMetadataFamily}
        onDocumentEditingChange={
          canEditUserEdits ? handleDocumentEditingChange : undefined
        }
        onPageEditingChange={handlePageEditingChange}
        onSelectVersion={canViewUserEdits ? handleSelectVersion : undefined}
        onOpenInNewTab={
          canViewUserEdits ? handleOpenVersionInNewTab : undefined
        }
        documentFallback={
          <PreviewDocumentDetailsSection
            document={documentTableRow}
            includeInternalIds={isGeckoUser}
          />
        }
        includeInternalIds={isGeckoUser}
        fillAvailable={fillAvailable}
      />
    </Box>
  )

  if (besideSubject && !scrollWithParent) {
    return <BesideSubjectShell>{section}</BesideSubjectShell>
  }

  return section
}
